import React, { useState, useEffect, useRef } from 'lib/react';
import {
  EVGrid,
  EVPaper,
  EVTypography,
  EVButton,
  EVKeyboardArrowDown,
  EVArrowBackIcon,
  EVWbSunnyOutlinedIcon,
  EVKeyboardArrowUp,
} from 'lib/components';
import {
  useParams, useLocation, Link,
} from 'lib/router';
import { useTranslation } from 'lib/translation';
import { useSelector, useDispatch } from 'lib/redux';
import useMediaQuery from 'hooks/useMediaQuery';
import { getOrderHistoryCells, isCompletedOrReadyToCapturePayment } from 'features/orderHistory/OrderHistory.utils';
import OrderHistoryStyles from 'features/orderHistory/OrderHistoryStyles';
import { ORDER_HISTORY_ROUTE } from 'constants/RouteConstants';
import UpgradeButton from 'features/orderHistory/components/UpgradeButton.component';
import RetryScreen from 'commonComponents/retry-screen';
import PageNotFound from 'commonComponents/pageNotFound';
import { PRODUCT_IDS } from 'constants/AppConstants';
import clsx from 'clsx';
import {
  isOrderingDisabledSelector,
  hasTrueDesignAppEntitlementSelector,
  isEntitledForEmbeddedExplorerSelector,
  featureFlagSelector,
  isSubscriptionDashboardV2EnabledSelector,
} from 'utils/appSelectors.utils';
import { ESX_FOR_UPGRADE } from 'constants/FeatureFlag.constants';
import ReportFilesDropdown from 'features/orderHistory/components/details-button/ReportFilesDropdown.component';
import { CLAIM_INFO_FIELDS } from 'features/orderHistory/OrderHistory.constants';
import Typography from 'commonComponents/typography';
import MapProviderToggle from 'commonComponents/map/components/MapProviderToggle.component';
import ReportDetailsPageStyles from './ReportDetailsPageStyles';
import ReportDetailsPageLoader from './components/ReportDetailsPageLoader';
import ReportDetailsMap from './components/reportDetailsMap';
import { loadReportDetailsPage, resetOrderHistoryErrors } from './ReportDetailsPage.actions';

function ReportDetailsPage() {
  const orderHistoryClasses = OrderHistoryStyles();
  const classes = ReportDetailsPageStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(640);
  const location = useLocation();
  const currentUrl = location.state?.currentUrl;
  const backUrl = currentUrl?.includes(ORDER_HISTORY_ROUTE) ? currentUrl : ORDER_HISTORY_ROUTE;

  const { reportId } = useParams();
  const {
    reportDetails,
    availableActions,
    propertyDataMap,
    loadError,
    error,
    notFound,
  } = useSelector(({ orderHistory, order }) => ({ ...orderHistory, error: order.error }));

  const {
    loading,
    mapError,
  } = useSelector(({ reportDetailsPage }) => reportDetailsPage);

  // #genai - optional chaining; still resolves to a falsy value when fileTypes is not loaded
  const reportFileTypes = useSelector(({ orderHistory: { fileTypes } }) => fileTypes?.[reportId]);

  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);

  const esxForUpgradeFlag = useSelector(featureFlagSelector(ESX_FOR_UPGRADE));
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const isOrderHistoryEsxFilesUpgradeEnabled = Boolean(esxForUpgradeFlag && !isSubscriptionDashboardV2Enabled);

  const fetchReportDeailsData = () => dispatch(loadReportDetailsPage(Number(reportId)));

  const details = reportDetails?.find(({ reportId: id }) => id === Number(reportId));

  const orderId = details?.orderId || null;

  // #genai - optional chaining, same truthiness guard as before
  const pinIndex = details?.building ? parseInt(details.building, 10) - 1 : 0;

  const isBlueprintOrder = details?.primaryProductID === PRODUCT_IDS.BlueprintResidential;

  const hasTrueDesignAppEntitlement = useSelector(hasTrueDesignAppEntitlementSelector);

  const [openFilesDropdown, setOpenFilesDropdown] = useState(false);
  const detailsButtonContainerRef = useRef();

  const handleFilesDropdownState = () => setOpenFilesDropdown((open) => !open);

  const [useEmbeddedExplorer, setUseEmbeddedExplorer] = useState(true);

  const isEntitledForEmbeddedExplorer = useSelector(isEntitledForEmbeddedExplorerSelector);

  useEffect(() => {
    dispatch(resetOrderHistoryErrors());
    if (!details) {
      fetchReportDeailsData();
    }
  }, []);

  if (notFound) return <PageNotFound />;

  if (loadError || error || mapError) {
    return <RetryScreen handleRetryButtonClick={fetchReportDeailsData} />;
  }

  if (!details || loading) {
    return <ReportDetailsPageLoader t={t} isMobile={isMobile} classes={classes} />;
  }

  const reportActions = availableActions[reportId];
  const [{
    address,
    status,
    product,
    delivery,
    date,
    expandedRow: claimInfoDetails,
  }] = details && getOrderHistoryCells([details], t, orderHistoryClasses, availableActions, false, {}, false, false, '', true, propertyDataMap || {});

  const claimInfoDetailsKeys = Object.keys(claimInfoDetails).filter((key) => key !== 'reportId');
  return (
    <EVPaper square={isMobile} className={classes.orderDetailsPaper}>
      <EVGrid container className={classes.orderDetailsContainer}>
        <EVGrid className={clsx(
          classes.orderDetailsHeaderContainer,
          isBlueprintOrder && classes.blueprintOrderDetails,
        )}
        >
          {!isMobile && (
          <EVGrid>
            <EVButton
              id="back-to-order-history-btn"
              color="primary"
              component={Link}
              to={backUrl}
              className={classes.backButton}
            >
              <EVArrowBackIcon />
              {t('report_details__back_to_order_history')}
            </EVButton>
          </EVGrid>
          )}

          <EVGrid className={classes.orderDetailsFiles}>
            <EVGrid
              className={clsx(
                classes.orderDetailsAddress,
                isBlueprintOrder && classes.blueprintOrderDetailsId,
              )}
            >
              <EVGrid className={!isBlueprintOrder && classes.orderDetailsHeading}>
                {address}
              </EVGrid>
              {status}
            </EVGrid>
            <EVGrid className={classes.headerButtons}>
              {hasTrueDesignAppEntitlement && reportActions?.enableTrueDesignLink && (
              <EVButton
                className={classes.trueDesignButton}
                id={`truedesign-${reportId}`}
                variant="outlined"
                color="primary"
                href={`/truedesign/${reportId}`}
              >
                <EVWbSunnyOutlinedIcon />
                {t('order_details_launch_trueDesign')}
              </EVButton>
              )}
              { reportFileTypes?.length > 0 && (
              <EVButton
                id={`filesDropdownBtn-${reportId}`}
                aria-label={`filesDropdownBtn-${reportId}`}
                variant="contained"
                color="primary"
                className={clsx(classes.filesDropdownBtn, openFilesDropdown && 'open')}
                onClick={handleFilesDropdownState}
                ref={detailsButtonContainerRef}
              >
                {t('order_history__actions__files')}
                {openFilesDropdown ? <EVKeyboardArrowUp /> : <EVKeyboardArrowDown />}
              </EVButton>
              )}
              <ReportFilesDropdown
                key={openFilesDropdown}
                reportId={Number(reportId)}
                handleClickAway={handleFilesDropdownState}
                open={openFilesDropdown}
                anchorEl={detailsButtonContainerRef.current}
                primaryProductID={details.primaryProductID}
                popperPlacement="bottom-end"
                showFilesEsxUpgrade={Boolean(
                  isOrderHistoryEsxFilesUpgradeEnabled
                  && reportActions?.canAddESX
                  && !isOrderingDisabled,
                )}
              />
              {
                isEntitledForEmbeddedExplorer && (
                  <MapProviderToggle
                    useEmbeddedExplorer={useEmbeddedExplorer}
                    onToggle={() => setUseEmbeddedExplorer(!useEmbeddedExplorer)}
                  />
                )
              }
            </EVGrid>
          </EVGrid>

        </EVGrid>
        <EVGrid className={classes.orderDetailsContent}>
          {!isBlueprintOrder && (
          <EVGrid className={classes.mapContainer}>
            <ReportDetailsMap pinIndex={pinIndex} orderId={orderId} useEmbeddedExplorer={useEmbeddedExplorer} />
          </EVGrid>
          )}
          <EVGrid className={classes.orderDetailsListItem}>
            <EVGrid container>
              <EVTypography className={classes.orderDetailsColumn}>{t('order_details__product')}</EVTypography>
              <EVGrid className={classes.productContainer}>
                <Typography style={{ width: '100%', alignSelf: 'center' }}>{product}</Typography>
                {reportActions?.canUpgradeReport && (
                <EVGrid className={classes.upgradeButton}>
                  {!isOrderingDisabled && isCompletedOrReadyToCapturePayment(details.statusID, details.subStatusID)
                  && <UpgradeButton reportId={reportId} />}
                </EVGrid>
                )}
              </EVGrid>
            </EVGrid>
            <EVGrid container>
              <EVTypography className={classes.orderDetailsColumn}>{t('order_details__delivery')}</EVTypography>
              <EVTypography>{delivery}</EVTypography>
            </EVGrid>
            <EVGrid container>
              <EVTypography className={classes.orderDetailsColumn}>{t('order_details__order_placed')}</EVTypography>
              <EVTypography>{date}</EVTypography>
            </EVGrid>
            <EVGrid container>
              <EVTypography className={classes.orderDetailsColumn}>{t('order_details__report')}</EVTypography>
              <EVTypography>{reportId}</EVTypography>
            </EVGrid>
            {Object.keys(claimInfoDetails)
              .filter((key) => claimInfoDetails[key] && claimInfoDetailsKeys.includes(key))
              .map((key) => (
                // #genai Filter first so the mapped element itself carries the list key (S6477).
                <EVGrid key={`claimInfo-${key}`} container>
                  <EVTypography className={classes.orderDetailsColumn}>{CLAIM_INFO_FIELDS[key]}</EVTypography>
                  <EVTypography>{claimInfoDetails[key]}</EVTypography>
                </EVGrid>
              ))}
            {details?.comments && (
            <EVGrid container>
              <EVTypography className={classes.orderDetailsColumn}>{t('order_details__comments')}</EVTypography>
              <EVTypography>{details?.comments}</EVTypography>
            </EVGrid>
            )}
          </EVGrid>
        </EVGrid>
      </EVGrid>
    </EVPaper>
  );
}

export default ReportDetailsPage;
